import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Stack,
  Text,
  Heading,
  Link,
  Box,
  UnorderedList,
  ListItem,
} from "@chakra-ui/react";
import { StyledButton } from "../styled-components/StyledButton";

export const PrivacyPage = () => {
  document.title = "Privacy Policy | SB Prop Bets";
  const navigate = useNavigate();

  return (
    <Stack spacing={4} align="center" pb="40px" px="24px">
      <Box paddingTop="40px">
        <StyledButton
          onClick={() => navigate("/home")}
          size="sm"
          useDarkMode
          colorScheme="brandColorScheme"
          variant="ghost"
        >
          &larr; Back to Home
        </StyledButton>
      </Box>

      <Heading fontSize={["2xl", "3xl"]} pt="20px">
        Privacy Policy
      </Heading>

      <Text fontSize="sm" color="gray.400">
        Last updated: February 1, 2025
      </Text>

      <Stack spacing={6} maxWidth="2xl" align="start">
        <Text>
          SB Prop Bets ("we", "us", or "our") operates the website sbpropbets.com.
          This page informs you of our policies regarding the collection, use, and
          disclosure of personal information when you use our Service.
        </Text>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Information We Collect
          </Heading>
          <Text mb={2}>
            When you use SB Prop Bets, we may collect the following information:
          </Text>
          <UnorderedList spacing={1} pl={4}>
            <ListItem>
              <strong>Email address</strong> - When you create a group or submit an entry
            </ListItem>
            <ListItem>
              <strong>Name</strong> - The name you provide for your entry
            </ListItem>
            <ListItem>
              <strong>Prop bet picks</strong> - Your answers to the prop bet questions
            </ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            How We Use Your Information
          </Heading>
          <Text mb={2}>We use your information to:</Text>
          <UnorderedList spacing={1} pl={4}>
            <ListItem>Provide and maintain the Service</ListItem>
            <ListItem>Send you confirmation emails when you create a group or submit an entry</ListItem>
            <ListItem>Send you reminders about upcoming events</ListItem>
            <ListItem>Send you occasional promotional emails about SB Prop Bets (you can unsubscribe anytime)</ListItem>
            <ListItem>Respond to your inquiries and support requests</ListItem>
          </UnorderedList>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Third-Party Services
          </Heading>
          <Text>
            We use the following third-party services to operate SB Prop Bets:
          </Text>
          <UnorderedList spacing={1} pl={4}>
            <ListItem>
              <strong>Firebase (Google)</strong> - For data storage and hosting
            </ListItem>
            <ListItem>
              <strong>Mailgun</strong> - For sending emails
            </ListItem>
          </UnorderedList>
          <Text mt={2}>
            These services may have access to your personal information only to perform
            tasks on our behalf and are obligated not to disclose or use it for other purposes.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Data Retention
          </Heading>
          <Text>
            We retain your information for as long as necessary to provide the Service
            and for a reasonable period thereafter. If you wish to have your data deleted,
            please contact us at{" "}
            <Link color="brandColorScheme.300" href="mailto:hello@sbpropbets.com">
              hello@sbpropbets.com
            </Link>.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Data Security
          </Heading>
          <Text>
            We take reasonable measures to protect your personal information. However,
            no method of transmission over the Internet or electronic storage is 100%
            secure, and we cannot guarantee absolute security.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Your Rights
          </Heading>
          <Text mb={2}>You have the right to:</Text>
          <UnorderedList spacing={1} pl={4}>
            <ListItem>Request access to your personal data</ListItem>
            <ListItem>Request correction of your personal data</ListItem>
            <ListItem>Request deletion of your personal data</ListItem>
            <ListItem>Unsubscribe from marketing emails at any time</ListItem>
          </UnorderedList>
          <Text mt={2}>
            To exercise any of these rights, contact us at{" "}
            <Link color="brandColorScheme.300" href="mailto:hello@sbpropbets.com">
              hello@sbpropbets.com
            </Link>.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Children's Privacy
          </Heading>
          <Text>
            Our Service is not intended for anyone under the age of 18. We do not
            knowingly collect personal information from children under 18.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Changes to This Policy
          </Heading>
          <Text>
            We may update this Privacy Policy from time to time. We will notify you
            of any changes by posting the new Privacy Policy on this page and updating
            the "Last updated" date.
          </Text>
        </Box>

        <Box>
          <Heading fontSize="lg" mb={2}>
            Contact Us
          </Heading>
          <Text>
            If you have any questions about this Privacy Policy, please contact us at{" "}
            <Link color="brandColorScheme.300" href="mailto:hello@sbpropbets.com">
              hello@sbpropbets.com
            </Link>.
          </Text>
        </Box>
      </Stack>
    </Stack>
  );
};
